import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useAuth } from "react-oidc-context";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import ServicoAcolhimentoService from "../../main/services/ServicoAcolhimentoService";
import PerfilAcessoService from "../../main/services/PerfilAcessoService";
import { criancaFunc } from "../../helpers/Funcionalidades";
import ProfileSelectionModal from "./ProfileSelectionModal";

const PROFILES_REQUIRING_ORGAO = new Set([
  "perfil-conselho_tutelar",
  "perfil-servico_acolhimento",
  "perfil-ministerio_publico",
  "perfil-defensoria_publica",
  "perfil-tribunal_justica",
]);

const PROFILE_TO_TIPO = {
  "perfil-conselho_tutelar": "CONSELHO TUTELAR REGIONAL RJ",
  "perfil-servico_acolhimento": "ABRIGO",
  "perfil-defensoria_publica": "DEFENSORIA PÚBLICA ESTADUAL",
  "perfil-tribunal_justica": "PODER JUDICIÁRIO - ESTADUAL TRIBUNAL",
};

const MP_PROFILE_NAME = "perfil-ministerio_publico";

const filtrarOrgaosPorPerfil = (profileName, orgaosExternos) => {
  const expectedTipo = PROFILE_TO_TIPO[profileName];
  return expectedTipo
    ? orgaosExternos.filter((o) => o.tipo === expectedTipo)
    : orgaosExternos;
};

const AppContext = createContext();

const SESSION_KEY = (sub) => `mca_selected_profile_${sub}`;
const SESSION_ORGAO_KEY = (sub) => `mca_selected_orgao_${sub}`;
const SESSION_AUTH_TIME_KEY = (sub) => `mca_auth_time_${sub}`;

const decodeJwt = (token) => {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join(""),
  );
  return JSON.parse(jsonPayload);
};

export const AuthProvider = ({ children }) => {
  const auth = useAuth();
  const history = useHistory();
  const location = useLocation();

  const [roleKeycloack, setRoleKeycloack] = useState([]);
  const [user, setUser] = useState({});
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedOrgao, setSelectedOrgao] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [orgaosDoUsuario, setOrgaosDoUsuario] = useState([]);
  const [promotoriasDoUsuario, setPromotoriasDoUsuario] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  // "login" → modal aberto no primeiro acesso; "context-switch" → troca de perfil voluntária
  const [profileModalSource, setProfileModalSource] = useState("login");

  const parsedTokenRef = useRef(null);

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;
    root.style.filter = showProfileModal ? "blur(8px)" : "";
    return () => {
      root.style.filter = "";
    };
  }, [showProfileModal]);

  const showValidationError = (mensagem) => {
    const root = document.getElementById("root");
    Swal.fire({
      title: "Atenção!",
      text: mensagem || "Erro de validação de perfil.",
      icon: "error",
      confirmButtonText: "Sair",
      confirmButtonColor: "#169499",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        if (root) root.style.filter = "blur(8px)";
      },
      willClose: () => {
        if (root) root.style.filter = "";
      },
    }).then(() => auth.signoutRedirect().catch(() => auth.removeUser()));
  };

  // Aplica o grupo e órgão escolhidos, atualiza contexto e sessionStorage
  const applyGroupProfile = async (group, orgao, payload) => {
    const roles = group.roles || [];

    // Precisa rodar antes de qualquer setState: no React 17, sem batching automático
    // fora do handler de evento síncrono, cada setState abaixo dispara um re-render
    // imediato — se os headers fossem setados depois, o useEffect do Dashboard
    // dispararia a requisição com o id_orgao_externo/id_perfil_selecionado antigos.
    if (group?.name === MP_PROFILE_NAME) {
      axios.defaults.headers.common["id_orgao"] = orgao?.id ?? "";
      delete axios.defaults.headers.common["id_orgao_externo"];
    } else {
      axios.defaults.headers.common["id_orgao_externo"] = orgao?.id ?? "";
      delete axios.defaults.headers.common["id_orgao"];
    }
    axios.defaults.headers.common["id_perfil_selecionado"] = group?.name ?? "";

    if (roles.includes(criancaFunc.visualizarTudoTutelar)) {
      try {
        const orgaoId = orgao?.id ?? payload?.orgaoExterno?.[0];
        const res =
          await ServicoAcolhimentoService.getOrgaoExternoConselho(orgaoId);

        const map = res.data.map((value) => value.id);
        setUser({
          ...payload,
          orgaoExterno: map,
          orgaoExternoOriginal: orgaoId ? [orgaoId] : [],
        });
      } catch {
        setUser({
          ...payload,
          orgaoExternoOriginal: orgao
            ? [orgao.id]
            : (payload.orgaoExterno ?? []),
        });
      }
    } else {
      setUser({
        ...payload,
        orgaoExternoOriginal: orgao ? [orgao.id] : (payload.orgaoExterno ?? []),
      });
    }

    setRoleKeycloack(roles);
    setSelectedProfile(group);
    setSelectedOrgao(orgao ?? null);
    setShowProfileModal(false);

    if (payload?.sub) {
      sessionStorage.setItem(SESSION_KEY(payload.sub), group.id);
      sessionStorage.setItem(
        SESSION_AUTH_TIME_KEY(payload.sub),
        String(payload.auth_time ?? ""),
      );
      if (orgao) {
        sessionStorage.setItem(
          SESSION_ORGAO_KEY(payload.sub),
          JSON.stringify(orgao),
        );
      } else {
        sessionStorage.removeItem(SESSION_ORGAO_KEY(payload.sub));
      }
    }
  };

  useEffect(() => {
    const parseToken = async () => {
      if (!auth?.user?.access_token) return;

      try {
        const parsedPayload = decodeJwt(auth.user.access_token);
        parsedTokenRef.current = parsedPayload;

        if (selectedProfile) {
          return;
        }

        // Busca órgãos do usuário via novo endpoint com todos os IDs do JWT
        let orgaosCarregados = [];
        const orgaoIds = parsedPayload.orgaoExterno ?? [];
        if (orgaoIds.length > 0) {
          try {
            const res =
              await PerfilAcessoService.listarOrgaosExternosVinculados(
                orgaoIds,
              );
            orgaosCarregados = (res.data ?? []).map((o) => ({
              id: o.id,
              nome: o.nome || o.razaoSocial || "",
              tipo: o.tipo?.nome ?? "",
              tipoId: o.tipo?.id ?? null,
            }));
          } catch {}
          setOrgaosDoUsuario(orgaosCarregados);
        }

        // Ministério Público: promotorias vêm da claim "orgaos" (não "orgaoExterno"),
        // array de objetos { id, tipoId, materias } — nomes são buscados à parte
        let promotoriasCarregadas = [];
        const promotoriasClaim = parsedPayload.orgaos ?? [];
        if (promotoriasClaim.length > 0) {
          try {
            const resPromotorias =
              await PerfilAcessoService.listarPromotoriasVinculadas();
            promotoriasCarregadas = (resPromotorias.data ?? []).map((o) => ({
              id: o.id,
              nome: o.nome || "",
            }));
          } catch {}
          setPromotoriasDoUsuario(promotoriasCarregadas);
        }

        // Novo formato: groups do scope mca-roles
        if (parsedPayload?.groups?.length) {
          const groups = parsedPayload.groups;
          setAvailableGroups(groups);

          // Validação de compatibilidade perfil/órgão — executa sempre
          try {
            const tipoIds = [
              ...new Set(orgaosCarregados.map((o) => o.tipoId).filter(Boolean)),
            ];
            const validacao = await PerfilAcessoService.verificarPerfilOrgao(
              groups.map((g) => g.name),
              tipoIds,
            );
            if (validacao.data?.valido === false) {
              showValidationError(validacao.data?.mensagem);
              return;
            }
          } catch {}

          // Restaurar seleção da sessão atual (mesma aba/refresh)
          // Se auth_time mudou = novo login → limpar sessão anterior e exibir modal
          const savedAuthTime = sessionStorage.getItem(
            SESSION_AUTH_TIME_KEY(parsedPayload.sub),
          );
          const currentAuthTime = String(parsedPayload.auth_time ?? "");
          if (savedAuthTime && savedAuthTime !== currentAuthTime) {
            Object.keys(sessionStorage)
              .filter(
                (k) =>
                  k.startsWith("mca_selected_") ||
                  k.startsWith("mca_auth_time_"),
              )
              .forEach((k) => sessionStorage.removeItem(k));
          } else {
            const savedId = sessionStorage.getItem(
              SESSION_KEY(parsedPayload.sub),
            );
            if (savedId) {
              const savedGroup = groups.find((g) => g.id === savedId);
              if (savedGroup) {
                const savedOrgaoStr = sessionStorage.getItem(
                  SESSION_ORGAO_KEY(parsedPayload.sub),
                );
                const savedOrgao = savedOrgaoStr
                  ? JSON.parse(savedOrgaoStr)
                  : null;
                await applyGroupProfile(savedGroup, savedOrgao, parsedPayload);
                return;
              }
            }
          }

          // Cenário 1 e 2: apenas um perfil → avaliar órgãos e pular modal se possível
          if (groups.length === 1) {
            const singleGroup = groups[0];
            const orgs =
              singleGroup.name === MP_PROFILE_NAME
                ? promotoriasCarregadas
                : PROFILES_REQUIRING_ORGAO.has(singleGroup.name)
                  ? filtrarOrgaosPorPerfil(singleGroup.name, orgaosCarregados)
                  : [];

            if (orgs.length === 0) {
              // Cenário 1: 1 perfil, 0 órgãos → validar e acessar direto
              try {
                const v = await PerfilAcessoService.verificarCompatibilidade(
                  singleGroup.name,
                  0,
                );
                if (v.data?.valido === false) {
                  showValidationError(v.data?.mensagem);
                  return;
                }
              } catch {}
              await applyGroupProfile(singleGroup, null, parsedPayload);
              return;
            }

            if (orgs.length === 1) {
              // Cenário 2: 1 perfil, 1 órgão → validar e acessar direto
              try {
                const v = await PerfilAcessoService.verificarCompatibilidade(
                  singleGroup.name,
                  orgs[0].tipoId ?? 0,
                );
                if (v.data?.valido === false) {
                  showValidationError(v.data?.mensagem);
                  return;
                }
              } catch {}
              await applyGroupProfile(singleGroup, orgs[0], parsedPayload);
              return;
            }

            // 1 perfil + múltiplos órgãos → exibir modal (perfil pré-selecionado/desabilitado)
          }

          setProfileModalSource("login");
          setShowProfileModal(true);
          return;
        }

        // Formato legado: resource_access.mca.roles
        const parsedKeyCloack = parsedPayload?.resource_access?.mca?.roles;
        if (parsedKeyCloack?.includes(criancaFunc.visualizarTudoTutelar)) {
          const res = await ServicoAcolhimentoService.getOrgaoExternoConselho(
            parsedPayload?.orgaoExterno[0],
          );
          console.log("res2", res);
          const map = res.data.map((value) => value.id);
          setUser({
            ...parsedPayload,
            orgaoExterno: map,
            orgaoExternoOriginal: parsedPayload.orgaoExterno
              ? [parsedPayload?.orgaoExterno[0]]
              : [],
          });
        } else {
          setUser({
            ...parsedPayload,
            orgaoExternoOriginal: parsedPayload.orgaoExterno
              ? [parsedPayload?.orgaoExterno[0]]
              : [],
          });
        }
        setRoleKeycloack(parsedKeyCloack);
      } catch (error) {
        console.error("Failed to parse JWT token", error);
      }
    };

    parseToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.access_token]);

  // Cenário 13: confirmação com perfil + órgão selecionados
  const handleProfileConfirm = async (group, orgao) => {
    if (!parsedTokenRef.current) return;
    const perfilMudou = selectedProfile?.id !== group?.id;
    const orgaoMudou = (selectedOrgao?.id ?? null) !== (orgao?.id ?? null);
    await applyGroupProfile(group, orgao ?? null, parsedTokenRef.current);
    // Só volta pro Dashboard se o Perfil ou o Órgão realmente mudaram
    if ((perfilMudou || orgaoMudou) && location.pathname !== "/dashboard") {
      history.push("/dashboard");
    }
  };

  const clearSessionProfile = () => {
    Object.keys(sessionStorage)
      .filter(
        (key) =>
          key.startsWith("mca_selected_") || key.startsWith("mca_auth_time_"),
      )
      .forEach((key) => sessionStorage.removeItem(key));
  };

  const logout = async () => {
    clearSessionProfile();
    try {
      await auth.signoutRedirect();
    } catch {
      auth.removeUser();
    }
  };

  // Cenário 17: logout ao fechar modal vindo do login
  const handleModalClose = async () => {
    if (profileModalSource === "login") {
      await logout();
    }
    setShowProfileModal(false);
  };

  // Cenário 16: troca de contexto voluntária
  const openProfileModal = () => {
    setProfileModalSource("context-switch");
    setShowProfileModal(true);
  };

  // Mapa groupId → lista de órgãos para o modal
  const orgaosMap = {};
  availableGroups.forEach((g) => {
    if (!PROFILES_REQUIRING_ORGAO.has(g.name)) {
      orgaosMap[g.id] = [];
      return;
    }
    if (g.name === MP_PROFILE_NAME) {
      // Ministério Público lista Promotorias, não Órgãos Externos
      orgaosMap[g.id] = promotoriasDoUsuario;
      return;
    }
    orgaosMap[g.id] = filtrarOrgaosPorPerfil(g.name, orgaosDoUsuario);
  });

  // Habilitado quando há mais de um perfil OU um único perfil com múltiplos órgãos/promotorias
  const canChangeProfile =
    availableGroups.length > 1 ||
    (availableGroups.length === 1 &&
      PROFILES_REQUIRING_ORGAO.has(availableGroups[0]?.name) &&
      (availableGroups[0]?.name === MP_PROFILE_NAME
        ? promotoriasDoUsuario.length > 1
        : filtrarOrgaosPorPerfil(availableGroups[0]?.name, orgaosDoUsuario)
            .length > 1));

  const contextValue = {
    roleKeycloack,
    user,
    selectedProfile,
    selectedOrgao,
    availableGroups,
    canChangeProfile,
    openProfileModal,
    logout,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {showProfileModal && (
        <ProfileSelectionModal
          groups={availableGroups}
          orgaosMap={orgaosMap}
          onConfirm={handleProfileConfirm}
          onClose={handleModalClose}
          isFromLogin={profileModalSource === "login"}
        />
      )}
      {children}
    </AppContext.Provider>
  );
};

export const useAuthProvider = () => useContext(AppContext);
